import type { CreateCardPayment } from "@b2bportal/payment-methods-api";
import {
  Payment,
  type PaymentOpaqueValue,
  Product,
  type ProductOpaqueValue,
} from "@b2bportal/purchase-api";
import {
  type AdditionalPointsInfoResponse,
  AdditionalPointsInfoResponseEnum,
  type Available,
} from "@b2bportal/rbc-additional-points-api";
import type { RbcPointsQuoteRequest } from "@b2bportal/rbc-points-api";
import { FlightSelectors } from "@checkout/states/Products/Flight";
import {
  getObjectKeysAsObject,
  type ICardPayment,
  ParentState,
} from "@hopper-b2b/checkout";
import { logger } from "@hopper-b2b/datadog";
import { assign, type DoneInvokeEvent } from "xstate";
import { OfferPaymentState, type WalletPaymentContext } from "../Wallet/types";
import { RewardsSelectedOptionType } from "./context";
import type {
  SetBasedFareFixedPriceEvent,
  SetPointsRedemptionEvent,
  SetPointsToCashLoadingEvent,
  SetRewardsAccountIdEvent,
  SetRewardsAmountToApplyEvent,
  SetRewardsSelected,
} from "./events";
import type { ParentContextWithRewardsPayment } from "./types";
import {
  getRewardsAmountCashEquivalent,
  getTripTotalFromQuoteBreakdownWithProducts,
} from "@/modules/flight-checkout/selectors";
import type { FlightMachineContext } from "@/modules/flight-checkout/types";
import {
  getRemainingBalanceAfterRewardsApplied,
  getIsFixedPriceFromBasePrice,
} from "@/utilities/rewards";

export interface ICreateCardPayment extends PaymentOpaqueValue {
  type: typeof Payment.Card;
  value: CreateCardPayment;
}

export interface RbcPointsProductType extends ProductOpaqueValue {
  type: typeof Product.RbcPoints;
  value: RbcPointsQuoteRequest;
}

export const actions = {
  closeAdditionalPointsNotEligibleModal: assign(
    (ctx: ParentContextWithRewardsPayment) => ({
      ...ctx,
      [ParentState.awardsPayment]: {
        ...ctx[ParentState.awardsPayment],
        showAdditionalPointsNotEligibleModal: false,
        requiresTravelerRevalidation: true,
      },
    })
  ),
  clearTravelerRevalidationFlag: assign(
    (ctx: ParentContextWithRewardsPayment) => ({
      ...ctx,
      [ParentState.awardsPayment]: {
        ...ctx[ParentState.awardsPayment],
        requiresTravelerRevalidation: false,
      },
    })
  ),
  setRewardsAmountToApply: assign(
    (
      ctx: ParentContextWithRewardsPayment,
      event: SetRewardsAmountToApplyEvent
    ) => ({
      ...ctx,
      [ParentState.awardsPayment]: {
        ...ctx[ParentState.awardsPayment],
        rewardsAmountToApply: event.rewardsAmountToApply,
      },
    })
  ),
  calculateRewardsAmountCashEquivalent: assign(
    (ctx: ParentContextWithRewardsPayment) => {
      const { rewardsAmountToApply } = ctx[ParentState.awardsPayment];
      const quoteBreakdown = ctx[ParentState.cartQuote].quoteBreakdown;

      if (!rewardsAmountToApply || !quoteBreakdown) {
        return {
          ...ctx,
          [ParentState.awardsPayment]: {
            ...ctx[ParentState.awardsPayment],
            rewardsAmountToApplyCashEquivalent: undefined,
          },
        };
      }

      // Cash value of the applied points from the pointsToCash/quote brackets
      const cashEquivalent = getRewardsAmountCashEquivalent(
        { context: ctx },
        rewardsAmountToApply.value
      );

      return {
        ...ctx,
        [ParentState.awardsPayment]: {
          ...ctx[ParentState.awardsPayment],
          rewardsAmountToApplyCashEquivalent: cashEquivalent,
        },
      };
    }
  ),
  setRewardsSelected: assign(
    (ctx: ParentContextWithRewardsPayment, event: SetRewardsSelected) => ({
      ...ctx,
      [ParentState.awardsPayment]: {
        ...ctx[ParentState.awardsPayment],
        rewardsSelectedState: event.selectedOption,
      },
    })
  ),
  // Override from shared checkout library setPaymentFulfillParams action
  setPaymentFulfillParams: assign(
    (ctx: FlightMachineContext & Partial<WalletPaymentContext>) => {
      // Wallet offer payment (hotel checkout; other funnels have no
      // OfferPaymentState in context yet). The BE computes the discount — the
      // offer is fulfilled by id only.
      const { selectedOffer, offerApplied } = ctx[OfferPaymentState] ?? {};
      let offerPayment: {
        type: Payment;
        value: { offerId: string };
      };
      if (offerApplied && selectedOffer != null) {
        offerPayment = {
          type: Payment.TravelWalletOffer,
          value: {
            offerId: selectedOffer.id,
          },
        };
      }

      // const { creditPaymentAmount } = ctx[TravelCreditPaymentState];
      // let creditPayment: {
      //   type: Payment;
      //   value: { paymentAmount: { amount: number; currency: string } };
      // };
      // if (creditPaymentAmount != null) {
      //   if (
      //     creditPaymentAmount.currencyCode !==
      //     totalPaymentBalance.fiat.currencyCode
      //   ) {
      //     throw new Error(
      //       `Currency mismatch: total: ${totalPaymentBalance.fiat.currencyCode} !== credit: ${creditPaymentAmount.currencyCode}`
      //     );
      //   }
      //   if (creditPaymentAmount.value > 0) {
      //     creditPayment = {
      //       type: Payment.TravelWalletCredit,
      //       value: {
      //         paymentAmount: {
      //           amount: creditPaymentAmount.value,
      //           currency: creditPaymentAmount.currencyCode,
      //         },
      //       },
      //     };
      //   }
      // }

      // calculate rewards payment
      const {
        rewardsAmountToApply,
        rewardsSelectedState,
        baseFareFixedPrice,
        rewardsAmountToApplyCashEquivalent,
      } = ctx[ParentState.awardsPayment];

      const numberOfFixedPoints = baseFareFixedPrice?.value || 0;
      const numberOfFlexPoints = rewardsAmountToApply?.value || 0;
      const isFixedPrice = numberOfFixedPoints > 0;

      const isFlexPointsRedemption =
        rewardsSelectedState === RewardsSelectedOptionType.APPLY_POINTS &&
        numberOfFlexPoints > 0;

      const awardsPayment =
        isFlexPointsRedemption || isFixedPrice
          ? {
              type: Payment.RbcAvionPoints,
              value: {
                numberOfPoints: numberOfFlexPoints + numberOfFixedPoints,
                pointsRedemptionBreakdown: {
                  numberOfFixedPoints,
                  numberOfFlexiblePoints: numberOfFlexPoints,
                },
              },
            }
          : undefined;

      const tripTotal = getTripTotalFromQuoteBreakdownWithProducts({
        context: ctx,
      });
      const remainingBalance = getRemainingBalanceAfterRewardsApplied(
        tripTotal,
        rewardsAmountToApply,
        rewardsAmountToApplyCashEquivalent
      );

      const cardPayment: ICardPayment =
        remainingBalance?.value > 0
          ? {
              type: Payment.Card,
              value: {
                paymentAmount: {
                  amount: remainingBalance.value,
                  currency: remainingBalance.currencyCode,
                },
                paymentId: ctx[ParentState.cardPayment].selectedPaymentMethodId,
              },
            }
          : undefined;

      // Log to Datadog when we're about to send undefined paymentId with a remaining balance
      if (
        remainingBalance?.value > 0 &&
        ctx[ParentState.cardPayment].selectedPaymentMethodId === undefined
      ) {
        logger()?.warn(
          "RBC fulfill: undefined paymentId with remaining balance",
          {
            remainingBalance: remainingBalance.value,
            flexPointsSent: numberOfFlexPoints,
            fixedPointsSent: numberOfFixedPoints,
            rewardsAmountToApply: rewardsAmountToApply?.value,
            tripTotalFiat: tripTotal?.fiat?.value,
            tripTotalPoints:
              tripTotal?.accountSpecific?.[
                ctx[ParentState.awardsPayment]?.rewardsAccountId
              ]?.value,
            paymentMethodsCount:
              ctx[ParentState.cardPayment].paymentMethods.length,
            rewardsAmountToApplyCashEquivalent:
              rewardsAmountToApplyCashEquivalent?.value,
          }
        );
      }

      const newPayments = [
        cardPayment,
        offerPayment,
        // creditPayment,
        awardsPayment,
      ].filter((payment): payment is PaymentOpaqueValue => payment != null);

      const existingPayments =
        ctx[ParentState.cartFulfill].fulfillRequestPayments;
      const fulfillRequestPayments = [
        ...existingPayments.filter(
          (p) => !newPayments.some((newP) => newP.type === p.type)
        ),
        ...newPayments,
      ];

      return {
        ...ctx,
        [ParentState.cartFulfill]: {
          ...ctx[ParentState.cartFulfill],
          fulfillRequestPayments,
        },
      };
    }
  ),
  setPointsRedemption: assign(
    (
      ctx: ParentContextWithRewardsPayment,
      event: SetPointsRedemptionEvent
    ) => ({
      ...ctx,
      [ParentState.awardsPayment]: {
        ...ctx[ParentState.awardsPayment],
        pointsRedemption: event.pointsRedemption,
        pointsToCashError: event.error,
        // Without brackets there's no cash value for the applied points, so
        // drop them rather than sending points with an unreduced card amount.
        ...(event.error ? { rewardsAmountToApply: undefined } : {}),
      },
    })
  ),
  setPointsToCashLoading: assign(
    (
      ctx: ParentContextWithRewardsPayment,
      event: SetPointsToCashLoadingEvent
    ) => ({
      ...ctx,
      [ParentState.awardsPayment]: {
        ...ctx[ParentState.awardsPayment],
        pointsToCashLoading: event.loading,
      },
    })
  ),
  setRewardsAccountId: assign(
    (
      ctx: ParentContextWithRewardsPayment,
      event: SetRewardsAccountIdEvent
    ) => ({
      ...ctx,
      [ParentState.awardsPayment]: {
        ...ctx[ParentState.awardsPayment],
        rewardsAccountId: event.rewardsAccountId,
      },
    })
  ),
  setBasedFareFixedPrice: assign(
    (
      ctx: ParentContextWithRewardsPayment,
      event: SetBasedFareFixedPriceEvent
    ) => ({
      ...ctx,
      [ParentState.awardsPayment]: {
        ...ctx[ParentState.awardsPayment],
        baseFareFixedPrice: event.baseFareFixedPrice,
      },
    })
  ),
  //TODO(Danii): we can use this later to automatically set the base fare fixed price after quoting - less fragile than our current implementation
  setBaseFareFixedPriceFromQuote: assign((ctx: FlightMachineContext) => {
    const pricingSummary = FlightSelectors.getQuoteBreakdownFlightPricing({
      context: ctx,
    });
    const totalPricing = pricingSummary?.totalPricing;
    if (!totalPricing?.baseWithoutMargin) {
      return ctx;
    }
    const rewardsAccountId =
      ctx[ParentState.awardsPayment]?.rewardsAccountId ?? "";
    if (
      !rewardsAccountId ||
      !getIsFixedPriceFromBasePrice(
        totalPricing.baseWithoutMargin,
        rewardsAccountId
      )
    ) {
      return ctx;
    }
    const baseFareFixedRewardsPrice =
      totalPricing.baseWithoutMargin.accountSpecific?.[rewardsAccountId];
    if (!baseFareFixedRewardsPrice) {
      return ctx;
    }

    return {
      ...ctx,
      [ParentState.awardsPayment]: {
        ...ctx[ParentState.awardsPayment],
        baseFareFixedPrice: baseFareFixedRewardsPrice,
      },
    };
  }),
  setAdditionalPointsPurchase: assign(
    (
      ctx: ParentContextWithRewardsPayment,
      { data }: DoneInvokeEvent<AdditionalPointsInfoResponse>
    ) => {
      // Check if NotEligible
      if (
        data.AdditionalPointsInfoResponse ===
        AdditionalPointsInfoResponseEnum.NotEligible
      ) {
        return {
          ...ctx,
          [ParentState.awardsPayment]: {
            ...ctx[ParentState.awardsPayment],
            showAdditionalPointsNotEligibleModal: true,
            additionalPointsPurchase: undefined,
          },
        };
      }

      // Check if Available
      if (
        data.AdditionalPointsInfoResponse ===
        AdditionalPointsInfoResponseEnum.Available
      ) {
        const productsToAdd = ctx[ParentState.cartUpdate].addProducts;
        const productsWithoutPointsPurchase =
          productsToAdd?.filter((p) => p.type !== Product.RbcPoints) || [];
        const pointsProduct: RbcPointsProductType = {
          type: Product.RbcPoints,
          value: {
            additionalPointsToPurchase: (data as Available)
              .numberOfPointsToPurchase,
          },
        };

        return {
          ...ctx,
          [ParentState.cartUpdate]: {
            ...ctx[ParentState.cartUpdate],
            addProducts: [...productsWithoutPointsPurchase, pointsProduct],
          },
          [ParentState.awardsPayment]: {
            ...ctx[ParentState.awardsPayment],
            additionalPointsPurchase: data as Available,
            showAdditionalPointsNotEligibleModal: false,
          },
        };
      }

      // NotNeeded or any other case
      return {
        ...ctx,
        [ParentState.awardsPayment]: {
          ...ctx[ParentState.awardsPayment],
          additionalPointsPurchase: undefined,
          showAdditionalPointsNotEligibleModal: false,
        },
      };
    }
  ),
  setAdditionalPointsPurchaseFromCombinedServices: assign(
    (
      ctx: ParentContextWithRewardsPayment,
      {
        data,
      }: DoneInvokeEvent<
        [unknown, unknown, AdditionalPointsInfoResponse | void]
      >
    ) => {
      // When called from combined services, data is an array: [validateResult, tripPricingResult, additionalPointsResult]
      // Validate array structure before accessing
      if (!Array.isArray(data) || data.length < 3) {
        return ctx;
      }

      const additionalPointsData = data[2];

      // If guard didn't pass, we get undefined from Promise.resolve()
      if (!additionalPointsData) {
        return ctx;
      }

      // Check if NotEligible
      if (
        additionalPointsData.AdditionalPointsInfoResponse ===
        AdditionalPointsInfoResponseEnum.NotEligible
      ) {
        return {
          ...ctx,
          [ParentState.awardsPayment]: {
            ...ctx[ParentState.awardsPayment],
            showAdditionalPointsNotEligibleModal: true,
            additionalPointsPurchase: undefined,
          },
        };
      }

      // Check if Available
      if (
        additionalPointsData.AdditionalPointsInfoResponse ===
        AdditionalPointsInfoResponseEnum.Available
      ) {
        const productsToAdd = ctx[ParentState.cartUpdate].addProducts;
        const productsWithoutPointsPurchase =
          productsToAdd?.filter((p) => p.type !== Product.RbcPoints) || [];
        const pointsProduct: RbcPointsProductType = {
          type: Product.RbcPoints,
          value: {
            additionalPointsToPurchase: (additionalPointsData as Available)
              .numberOfPointsToPurchase,
          },
        };

        return {
          ...ctx,
          [ParentState.cartUpdate]: {
            ...ctx[ParentState.cartUpdate],
            addProducts: [...productsWithoutPointsPurchase, pointsProduct],
          },
          [ParentState.awardsPayment]: {
            ...ctx[ParentState.awardsPayment],
            additionalPointsPurchase: additionalPointsData as Available,
            showAdditionalPointsNotEligibleModal: false,
          },
        };
      }

      // NotNeeded or any other case
      return {
        ...ctx,
        [ParentState.awardsPayment]: {
          ...ctx[ParentState.awardsPayment],
          additionalPointsPurchase: undefined,
          showAdditionalPointsNotEligibleModal: false,
        },
      };
    }
  ),
};

export const ActionTypes = getObjectKeysAsObject(actions);
